import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { type GHLAnalysis, formatRelativeTime, truncateId, formatCost, calculateAnalysisCost, calculateWhisperCost, formatTokenCount, getActionColor, getSkippedByToggle, getSkipReasonLabel } from '@/lib/ghl-dashboard'
import { useGHLContactNames } from '@/hooks/useGHLDashboard'
import { ContactAvatar } from './ContactAvatar'

interface Props {
  readonly data: GHLAnalysis[]
  readonly count: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly onPageChange: (page: number) => void
  readonly locationNames?: Map<string, string>
  /**
   * Optional override when the parent has a selected location in its filter.
   * Falls back to the first row's location_id when not provided.
   */
  readonly locationId?: string
}

function LoadingSkeleton({ rows = 5, cols = 10 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: cols }).map((_, j) => (
            <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

/**
 * Compact status pill cluster showing distinct-opportunity counts per terminal
 * state for the conversation: 🟢 booked, 🟡 open (quoted / in-progress),
 * 🔴 lost. Each pill is suppressed when its count is 0; all-zero displays "—".
 */
function PipelinePills({ booked, open, lost }: { booked: number; open: number; lost: number }) {
  if (booked === 0 && open === 0 && lost === 0) {
    return <span className="text-muted-foreground/40 text-xs">—</span>
  }
  return (
    <div className="flex items-center gap-1 text-xs">
      {booked > 0 && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-medium"
          title={`${booked} booked opportunit${booked === 1 ? 'y' : 'ies'}`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {booked}
        </span>
      )}
      {open > 0 && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 font-medium"
          title={`${open} open opportunit${open === 1 ? 'y' : 'ies'} (quoted / in-progress)`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          {open}
        </span>
      )}
      {lost > 0 && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-1.5 py-0.5 font-medium"
          title={`${lost} lost opportunit${lost === 1 ? 'y' : 'ies'}`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
          {lost}
        </span>
      )}
    </div>
  )
}

export function ActivityFeedTable({ data, count, page, pageSize, isLoading, onPageChange, locationNames, locationId }: Props) {
  const navigate = useNavigate()

  const totalPages = Math.ceil(count / pageSize)
  const from = page * pageSize + 1
  const to = Math.min((page + 1) * pageSize, count)

  // Gather contact IDs for batched name resolution. We use the selected
  // location when available; otherwise we best-effort with the first row's
  // location_id (usually correct since the feed is typically single-location).
  const contactIds = useMemo(
    () => [...new Set(data.map((row) => row.contact_id).filter(Boolean))],
    [data]
  )
  const effectiveLocationId = locationId ?? data[0]?.location_id
  const { data: contactNames } = useGHLContactNames(contactIds, effectiveLocationId)

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Action</TableHead>
              <TableHead title="Distinct opportunities per terminal state in this conversation">Pipeline</TableHead>
              <TableHead className="text-right">Confidence</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">Tokens</TableHead>
              <TableHead className="text-right">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <LoadingSkeleton />}
            {!isLoading && data.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                  No analyses found
                </TableCell>
              </TableRow>
            )}
            {!isLoading && data.map((row) => {
              const actionColor = getActionColor(row.recommended_action)
              const claudeCost = calculateAnalysisCost(row.input_tokens, row.output_tokens, row.model_used)
              const whisperCost = calculateWhisperCost(row.whisper_duration_seconds)
              const totalCost = claudeCost + whisperCost
              const name = contactNames?.get(row.contact_id)

              return (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/admin/ghl-middleware/analysis/${row.id}`)}
                >
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatRelativeTime(row.created_at)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {locationNames?.get(row.location_id) ?? truncateId(row.location_id)}
                  </TableCell>
                  <TableCell>
                    <ContactAvatar
                      size="sm"
                      showName
                      firstName={name?.firstName}
                      lastName={name?.lastName}
                      fullName={name?.fullName}
                      contactId={row.contact_id}
                    />
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1.5">
                      <span>{row.trigger_event.replace('Message', '')}</span>
                      {row.sibling_count !== undefined && row.sibling_count > 1 && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1 py-0 h-4 font-normal text-muted-foreground"
                          title={`${row.sibling_count} events in this conversation — click to see timeline`}
                        >
                          +{row.sibling_count - 1}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      <Badge className={`${actionColor.bg} ${actionColor.text} text-xs`}>
                        {actionColor.label}
                      </Badge>
                      {(() => {
                        // Suppression chip: tells the operator at-a-glance that
                        // even though Claude recommended this action, one or
                        // more intended writes were blocked by per-location
                        // toggles. Tooltip lists which gates fired.
                        const skipped = getSkippedByToggle(row.action_result)
                        if (skipped.length === 0) return null
                        const tooltip = skipped.map(getSkipReasonLabel).join(', ')
                        return (
                          <Badge
                            variant="outline"
                            className="bg-amber-50 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0 h-4 font-normal"
                            title={`Suppressed by toggle: ${tooltip}`}
                          >
                            Suppressed ({skipped.length})
                          </Badge>
                        )
                      })()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <PipelinePills
                      booked={row.booked_count ?? 0}
                      open={row.open_count ?? 0}
                      lost={row.lost_count ?? 0}
                    />
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    <span className={
                      row.booking_confidence >= 85 ? 'text-green-600 font-semibold' :
                      row.booking_confidence >= 50 ? 'text-yellow-600' : 'text-muted-foreground'
                    }>
                      {row.booking_confidence}%
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {row.price_quoted !== null ? (
                      <span className={row.price_confirmed ? 'text-green-600 font-semibold' : ''}>
                        ${row.price_quoted.toLocaleString()}
                        {row.price_confirmed && ' ✓'}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">--</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">
                    {formatTokenCount(row.input_tokens)}/{formatTokenCount(row.output_tokens)}
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    {whisperCost > 0 ? (
                      <div className="space-y-0.5">
                        <div className="text-muted-foreground">{formatCost(totalCost)}</div>
                        <div className="text-[10px] text-muted-foreground/60">
                          C:{formatCost(claudeCost)} W:{formatCost(whisperCost)}
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">{formatCost(claudeCost)}</span>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {count > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">
            Showing {from}-{to} of {count.toLocaleString()}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={(e) => { e.stopPropagation(); onPageChange(0) }}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page === 0} onClick={(e) => { e.stopPropagation(); onPageChange(page - 1) }}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm px-2">
              Page {page + 1} of {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={(e) => { e.stopPropagation(); onPageChange(page + 1) }}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={(e) => { e.stopPropagation(); onPageChange(totalPages - 1) }}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
